import { AlkLogger } from '@alk/core';
import { checkFeed } from '../../../helpers/communication-utils';
import { COMMUNICATION_SERVICE, ICommunicationService } from '../../communication';
import { compact, pluckTyped } from '../../../helpers';
import {
    decodeHistorical,
    decodeRealtime,
    historicalTransformator,
    incrementTransformator,
    validateNotification,
} from './notifications-adapter.service.utils';
import { finalize, map, share, switchMap, takeUntil, tap } from 'rxjs/operators';
import { HistoricalNotificationsRequest, MessageTypes, NotificationsRequest } from 'sfx-web-contract';
import { inject, injectable } from 'inversify';
import { INotificationsAdapter } from './notifications-adapter.interface';
import { ISessionLifecycle, SESSION_LIFECYCLE_SERVICE } from '../../session-lifecycle';
import { merge, Observable, of } from 'rxjs';
import { NotificationMessagesListTransaction } from '../../../interfaces';

@injectable()
export class NotificationsAdapterService implements INotificationsAdapter {
    @inject(COMMUNICATION_SERVICE) private readonly communicationService: ICommunicationService;
    private resultNotificationFeed$: Observable<NotificationMessagesListTransaction>;
    @inject(SESSION_LIFECYCLE_SERVICE) private readonly sessionLifeCycle: ISessionLifecycle;

    notificationsObservable = (): Observable<NotificationMessagesListTransaction> =>
        this.resultNotificationFeed$ || (this.resultNotificationFeed$ =
            merge(
                of(undefined).pipe(
                    map(this.createRealTimeRequest),
                    switchMap(this.getRealtimeObs),
                ),
                of(undefined).pipe(
                    map(this.createHistoricalRequest),
                    switchMap(this.getHistoricalObs),
                ),
            ).pipe(
                takeUntil(this.sessionLifeCycle.destroyObservable()),
                finalize(this.destroyHandler),
                share(),
            ));

    private readonly getRealtimeObs = (token: number): Observable<NotificationMessagesListTransaction> =>
        this.communicationService.dataObservable(MessageTypes.Notifications).pipe(
            checkFeed(token, '[NotificationsAdapterService#getRealtimeObs]'),
            pluckTyped('payload'),
            compact(),
            map(decodeRealtime),
            tap(notifications => notifications?.map(validateNotification)),
            map(incrementTransformator),
            tap(data => AlkLogger.debug(`[NotificationsAdapterService#getRealtimeObs] received: ${ JSON.stringify(data) }`)),
        );

    private readonly getHistoricalObs = (token: number): Observable<NotificationMessagesListTransaction> =>
        this.communicationService.dataObservable(MessageTypes.HistoricalNotifications).pipe(
            checkFeed(token, '[NotificationsAdapterService#getHistoricalObs]'),
            pluckTyped('payload'),
            compact(),
            map(decodeHistorical),
            map(historicalTransformator),
            tap(data => AlkLogger.debug(`[NotificationsAdapterService#getHistoricalObs] received: ${ JSON.stringify(data) }`)),
        );

    private readonly destroyHandler = (): void => {
        AlkLogger.debug('[NotificationsAdapterService#destroyHandler] service stopped');
        delete this.resultNotificationFeed$;
    };

    private readonly createRealTimeRequest = (): number => {

        AlkLogger.debug('[NotificationsAdapterService#createRealTimeRequest] is about to create subscription');

        return this.communicationService.create(
            NotificationsRequest.encode(NotificationsRequest.create({})).finish(),
            MessageTypes.Notifications,
        );
    };

    private readonly createHistoricalRequest = (): number => {
        AlkLogger.debug('[NotificationsAdapterService#createHistoricalRequest] is about to create subscription');

        return this.communicationService.create(
            HistoricalNotificationsRequest.encode(HistoricalNotificationsRequest.create({})).finish(),
            MessageTypes.HistoricalNotifications,
        );
    };
}
